// HelpCenterPage.js - 帮助中心独立页面（永久链接）
// 依据用户提供的文案创建；内部链接指向现有 /privacy 与 /terms

import React from 'react';

export default function HelpCenterPage() {
  return (
    <main className='max-w-3xl mx-auto px-6 py-12 text-gray-800'>
      <h1 className='text-3xl font-bold mb-4'>
        Help Center — TAG (TAG Art Guide)
      </h1>
      <p className='mb-8'>
        Welcome to the TAG Help Center! Here you can find quick answers about
        using TAG — posting your work, starting a project, and managing your
        profile.
        <br />
        Need more help? Contact us anytime at{' '}
        <a href='mailto:tag@rainwang.art' className='text-blue-600 underline'>
          tag@rainwang.art
        </a>
        .
      </p>

      {/* Getting Started */}
      <section id='getting-started' className='mb-10'>
        <h2 className='text-2xl font-semibold mb-3'>1) Getting Started</h2>
        <p>
          <strong>What is TAG?</strong>
          <br />
          TAG (TAG Art Guide) is a platform built by students, for students — a
          place to showcase creative work, find collaborators, and join projects
          across disciplines.
        </p>
        <p className='mt-3'>
          <strong>How do I create an account?</strong>
          <br />
          Click <b>Sign Up</b> on the homepage, verify your email, and complete
          your profile. Once verified, you can start uploading works and
          creating projects.
        </p>
        <p className='mt-3'>
          <strong>How do I edit my profile?</strong>
          <br />
          Go to your <b>Profile</b> page → click <b>Edit Profile</b>. You can
          update your name, school, major, bio, and social links anytime. All
          changes are auto-saved.
        </p>
      </section>

      {/* Gallery */}
      <section id='gallery' className='mb-10'>
        <h2 className='text-2xl font-semibold mb-3'>2) Gallery (Your Works)</h2>
        <p>
          <strong>How do I upload a new work?</strong>
          <br />
          Go to <b>Gallery → Upload</b>, choose your files (image/video), and
          add a title, tags, and description.
          <br />
          Accepted formats: JPG, PNG, MP4 (under 50 MB each).
        </p>
        <p className='mt-3'>
          <strong>Can I edit or delete my work later?</strong>
          <br />
          Yes. Open your work page → click <b>Edit</b> or <b>Delete</b> from the
          options menu.
        </p>
      </section>

      {/* Project */}
      <section id='project' className='mb-10'>
        <h2 className='text-2xl font-semibold mb-3'>
          3) Project (Collaboration System)
        </h2>
        <p>
          <strong>What is a Project?</strong>
          <br />
          Projects let you collaborate with other students — share ideas, build
          teams, and track milestones.
        </p>
        <p className='mt-3'>
          <strong>How to create a new project?</strong>
          <br />
          Go to <b>Projects → Create Project</b>. Add a project title,
          description, and optional cover image. You can also set your project
          visibility (public or invite-only).
        </p>
        <p className='mt-3'>
          <strong>How do others join my project?</strong>
          <br />
          When your project is public, other users can click <b>Apply</b> to
          join. You’ll receive a notification to approve or decline requests.
        </p>
        <p className='mt-3'>
          <strong>How to track progress?</strong>
          <br />
          Use <b>Milestones</b> to record goals, uploads, and discussions. Each
          milestone can include text, images, and comments.
        </p>
      </section>

      {/* Account & Privacy */}
      <section id='account' className='mb-10'>
        <h2 className='text-2xl font-semibold mb-3'>4) Account & Privacy</h2>
        <p>
          <strong>How do I change my password?</strong>
          <br />
          Go to <b>Profile → Settings</b>, and use <b>Reset Password</b>. We’ll
          send a secure email link to update your credentials.
        </p>
        <p className='mt-3'>
          <strong>How do I delete my account?</strong>
          <br />
          Send an email to{' '}
          <a href='mailto:tag@rainwang.art' className='text-blue-600 underline'>
            tag@rainwang.art
          </a>{' '}
          with the subject “Account Deletion Request.” Your data will be deleted
          or anonymized within 30–90 days.
          <br />
          See our{' '}
          <a href='/privacy' className='text-blue-600 underline'>
            Privacy Policy
          </a>{' '}
          for more details.
        </p>
      </section>

      {/* Troubleshooting */}
      <section id='troubleshooting' className='mb-10'>
        <h2 className='text-2xl font-semibold mb-3'>5) Troubleshooting</h2>
        <p>
          <strong>I can’t log in. What should I do?</strong>
          <br />
          Make sure your email is verified and cookies are enabled. If the
          problem persists, clear your browser cache or try Chrome / Firefox
          latest version.
        </p>
        <p className='mt-3'>
          <strong>My images won’t load.</strong>
          <br />
          Refresh the page. If the issue continues, it may be due to CDN
          caching. Send details (file name, browser, and screenshot) to{' '}
          <a href='mailto:tag@rainwang.art' className='text-blue-600 underline'>
            tag@rainwang.art
          </a>
          .
        </p>
      </section>

      {/* Feedback & Support */}
      <section id='feedback' className='mb-10'>
        <h2 className='text-2xl font-semibold mb-3'>6) Feedback & Support</h2>
        <p>
          We’re always improving TAG! If you find bugs, have feature
          suggestions, or want to join our ThinkTank testing team — contact{' '}
          <a href='mailto:tag@rainwang.art' className='text-blue-600 underline'>
            tag@rainwang.art
          </a>{' '}
          with subject line <b>Feedback</b> or <b>Bug Report</b>.
        </p>
      </section>

      {/* Footer */}
      <footer className='border-t pt-6 text-sm text-gray-500'>
        <p>© 2025 TAG Art Guide (TAG). All rights reserved.</p>
        <p>
          <a href='/privacy' className='underline mr-4'>
            Privacy Policy
          </a>
          <a href='/terms' className='underline'>
            Terms of Service
          </a>
        </p>
        <p className='mt-2'>Built by students. For students.</p>
      </footer>
    </main>
  );
}
